"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recolorHair = exports.colorRamp = exports.prepareHair = void 0;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
function prepareHair(pixels, mask, width, height) {
    const n = width * height;
    if (!Number.isInteger(n) || width < 1 || height < 1 || n > 1600000 || pixels.length !== n * 4 || mask.length !== n)
        throw new Error("Invalid prepared photo");
    const histogram = new Uint32Array(256);
    const luminance = new Uint8Array(n);
    let count = 0;
    for (let i = 0; i < n; i++) {
        const j = i * 4;
        luminance[i] = Math.round(.299 * pixels[j] + .587 * pixels[j + 1] + .114 * pixels[j + 2]);
        if (mask[i] > 230) {
            histogram[luminance[i]]++;
            count++;
        }
    }
    if (count / n < .015)
        throw new Error("We couldn't isolate enough hair. Try a photo with your hair clearly visible.");
    const percentile = (percent) => {
        let total = 0;
        for (let i = 0; i < 256; i++) {
            total += histogram[i];
            if (total >= count * percent)
                return Math.max(1, i);
        }
        return 200;
    };
    const reference = percentile(.92);
    const median = percentile(.5) / reference;
    const gamma = median > .02 && median < .98 ? clamp(Math.log(.72) / Math.log(median), .4, 1.35) : 1;
    return { pixels, mask, luminance, width, height, reference, gamma };
}
exports.prepareHair = prepareHair;
function colorRamp(hex, reference, gamma) {
    if (!/^#[a-f0-9]{6}$/i.test(hex))
        throw new Error("Invalid shade");
    const rgb = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
    const [r, g, b] = rgb, v = Math.max(...rgb), min = Math.min(...rgb), d = v - min;
    const saturation = v ? d / v : 0;
    let hue = d ? (v === r ? (g - b) / d : v === g ? (b - r) / d + 2 : (r - g) / d + 4) / 6 : 0;
    if (hue < 0)
        hue++;
    const ramp = new Uint8ClampedArray(256 * 3);
    for (let l = 0; l < 256; l++) {
        const ratio = clamp(Math.pow(l / Math.max(1, reference), gamma), .12, 1.7);
        const referenceV = Math.min(v, .94);
        const value = ratio <= 1 ? referenceV * ratio : referenceV + (1 - referenceV) * (1 - Math.exp(-2 * (ratio - 1)));
        const highlight = clamp((ratio - 1) / .7, 0, 1), shadow = clamp((1 - ratio) / .88, 0, 1);
        const s = Math.min(1, saturation * (1 - .28 * highlight ** 2) * (1 + .05 * shadow)) * (1 - .18 * shadow ** 3);
        const h = hue <= .22 ? hue + (.055 - hue) * .30 * shadow : hue;
        const hh = ((h % 1) + 1) % 1 * 6, sector = Math.floor(hh), f = hh - sector;
        const p = value * (1 - s), q = value * (1 - s * f), t = value * (1 - s * (1 - f));
        const c = [[value, t, p], [q, value, p], [p, value, t], [p, q, value], [t, p, value], [value, p, q]][sector % 6];
        for (let k = 0; k < 3; k++)
            ramp[l * 3 + k] = Math.round(c[k] * 255);
    }
    return ramp;
}
exports.colorRamp = colorRamp;
function recolorHair(base, hex) {
    const output = new Uint8ClampedArray(base.pixels);
    if (!hex)
        return output;
    const ramp = colorRamp(hex, base.reference, base.gamma);
    for (let i = 0; i < base.mask.length; i++) {
        if (!base.mask[i])
            continue;
        const alpha = base.mask[i] / 255, j = i * 4, index = base.luminance[i] * 3;
        for (let k = 0; k < 3; k++)
            output[j + k] = Math.round(base.pixels[j + k] * (1 - alpha) + ramp[index + k] * alpha);
    }
    return output;
}
exports.recolorHair = recolorHair;
